import { CHART_ELEMENT_ID } from 'finviz-charts/app/constants/common'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import { BrowserRouter } from 'react-router-dom'

import { NotificationWrapper } from '../../main/components/notification'
import { SpecificChartFunctionality, TIMEFRAME } from '../../main/constants'
import { useCheckNonIdeaChartMissingConfig } from '../../main/hooks/use-check-non-idea-chart-missing-config'
import { initIdeaQuotes } from '../../main/modules/charts/util'
import { ChartLayoutWithGlobalState, FREE_DATE_RANGES, Quote, Theme } from '../charts-lib'
import { decodeQueryString } from '../queryString'
import { getInitChartConfig } from '../shared/getInitChartConfig'
import { ready } from '../shared/ready'
import ETFHoldings from './etf-holdings'
import FundamentalCharts from './fundamentalCharts'
import { Perf } from './quote'
import { ChartContextQuoteHeader, ManualInitQuoteHeader } from './quote-header'
import { QUOTE_HEADER_ROOT } from './quote-header/constants'
import CreateAlertLink from './quote-links/CreateAlertLink'
import SaveToPortfolioLink from './quote-links/SaveToPortfolioLink'
import { QuoteMenu } from './quote-menu'
import Statements from './statements'
import { getChartLayoutSizeConfig, useChartLayoutWidthWatch } from './use-chart-layout-width-watch'
import { useStockDetailUpdater } from './use-stock-detail-updater'

if (FinvizSettings.hasUserPremium || FinvizSettings.hasRedesignEnabled) {
  import(/* webpackChunkName: "recent_quotes" */ '../shared/recent_quotes').then(({ RenderRecentQuotes }) => {
    RenderRecentQuotes()
  })
}

const QuoteMenuPortal = ({ isStockDetailUpdating }: { isStockDetailUpdating: boolean }) => {
  const quoteMenuElements = document.getElementsByClassName('quote-menu')
  const quoteMenuHtmlElements = Array.from(quoteMenuElements).filter((el) => el instanceof HTMLElement) as HTMLElement[]

  if (quoteMenuHtmlElements.length > 0) {
    return (
      <>
        {quoteMenuHtmlElements.map((quoteMenuElement: HTMLElement, quoteMenuIndex: number) => {
          const menuIndex = quoteMenuIndex + 1
          return ReactDOM.createPortal(
            <QuoteMenu
              index={menuIndex}
              isInteractiveChart={menuIndex === 0}
              isStockDetailUpdating={isStockDetailUpdating}
            />,
            quoteMenuElement
          )
        })}
      </>
    )
  }
  return null
}

const MENU_LINKS = [
  {
    selector: 'js-save-to-portfolio-link',
    LinkComponent: SaveToPortfolioLink,
  },
  {
    selector: 'js-create-alert-link',
    LinkComponent: CreateAlertLink,
  },
]

const RenderMenuLinks = () => {
  MENU_LINKS.forEach(({ selector, LinkComponent }) => {
    const linkElements = document.getElementsByClassName(selector)
    const linkHtmlElements = Array.from(linkElements).filter((el) => el instanceof HTMLElement) as HTMLElement[]

    linkHtmlElements.forEach((linkElement, index) => {
      ReactDOM.render(<LinkComponent index={index} />, linkElement)
    })
  })
}

const ChartLayoutPortal = () => {
  useChartLayoutWidthWatch()
  const chartEl = document.getElementById(CHART_ELEMENT_ID)
  const chartConfig = window.globalChartConfig
  const theme = FinvizSettings.hasDarkTheme ? Theme.dark : Theme.light

  if (chartEl && chartConfig) {
    const { p = FinvizSettings.defaultPeriod as TIMEFRAME } = decodeQueryString<{ p?: TIMEFRAME }>()
    return ReactDOM.createPortal(
      <ChartLayoutWithGlobalState
        config={{
          ...chartConfig,
          ...getChartLayoutSizeConfig(chartConfig, p),
          specificChartFunctionality: SpecificChartFunctionality.quotePage,
          charts: chartConfig.charts.map((chart) => ({
            ...chart,
            dateRange:
              FinvizSettings.hasUserPremium || FREE_DATE_RANGES.includes(chart.dateRange ?? '')
                ? chart.dateRange
                : null,
          })),
          theme,
        }}
        parentElement={chartEl}
      />,
      chartEl
    )
  }
  return null
}

enum QuotePageType {
  StockDetail = 'stockDetail',
  Idea = 'idea',
  Perf = 'perf',
  Options = 'options',
}

function getQuotePageType() {
  const { ty, i } = decodeQueryString()

  if (ty === 'p') {
    return QuotePageType.Perf
  }
  if (ty === 'oc') {
    return QuotePageType.Options
  }
  if (i) {
    return QuotePageType.Idea
  }

  return QuotePageType.StockDetail
}

function getQuoteHeaderElement() {
  const quotePageType = getQuotePageType()

  if (quotePageType === QuotePageType.StockDetail) {
    return (
      <Suspense fallback={null}>
        <ChartContextQuoteHeader />
      </Suspense>
    )
  }

  const config = window.globalChartConfig
  if (config) {
    return <ManualInitQuoteHeader config={config} />
  }

  return null
}

const ReactComponentsContainer = () => {
  const { isStockDetailUpdating } = useStockDetailUpdater()
  const quotePageType = getQuotePageType()
  const isPerfQuotePage = quotePageType === QuotePageType.Perf
  const isOptionsQuotePage = quotePageType === QuotePageType.Options
  const hasConfig = window.globalChartConfig

  useCheckNonIdeaChartMissingConfig()

  return (
    <NotificationWrapper>
      {hasConfig || isPerfQuotePage || isOptionsQuotePage ? (
        <Suspense fallback={null}>
          <QuoteMenuPortal isStockDetailUpdating={isStockDetailUpdating} />
        </Suspense>
      ) : null}
      {hasConfig && !isOptionsQuotePage && !isPerfQuotePage && (
        <Suspense fallback={null}>
          <ChartLayoutPortal />
        </Suspense>
      )}
    </NotificationWrapper>
  )
}

ready(() => {
  const statementsEl = document.getElementById('statements')
  if (statementsEl && window['fetch']) {
    ReactDOM.render(<Statements />, statementsEl)
  }

  const etfHoldingsEl = document.getElementById('js-etf-holdings')
  if (etfHoldingsEl) {
    ReactDOM.render(
      <BrowserRouter>
        <ETFHoldings />
      </BrowserRouter>,
      etfHoldingsEl
    )
  }

  const faEl = document.getElementById('js-quote-fa')
  if (faEl) {
    ReactDOM.render(<FundamentalCharts />, faEl)
  }

  const reactRootElement = document.getElementById('react-root')
  const quoteHeaderRoot = document.getElementById(QUOTE_HEADER_ROOT)

  if (FinvizSettings.quoteData) {
    Quote.create(FinvizSettings.quoteData)
  }

  if (window.globalChartConfig && !window.globalChartConfig.idea) {
    window.globalChartConfig = getInitChartConfig()
  }

  if (reactRootElement) {
    if (window.globalChartConfig?.ideaQuotes) {
      initIdeaQuotes(window.globalChartConfig.ideaQuotes, window.globalChartConfig)
    }

    ReactDOM.render(
      <BrowserRouter>
        <ReactComponentsContainer />
      </BrowserRouter>,
      reactRootElement
    )
  }

  if (quoteHeaderRoot) {
    const quoteHeaderElement = getQuoteHeaderElement()
    if (quoteHeaderElement) {
      ReactDOM.render(quoteHeaderElement, quoteHeaderRoot)
    }
  }

  RenderMenuLinks()

  if (document.getElementById('app') && window['FinvizQuoteTickers']) {
    ReactDOM.render(<Perf />, document.getElementById('app'))
  }
})

const revealRatingButtonsCollection = document.getElementsByClassName('js-reveal-ratings-button')
const revealRatingButtons = Array.from(revealRatingButtonsCollection) as HTMLButtonElement[]
revealRatingButtons.forEach((revealRatingButton) => {
  revealRatingButton.onclick = (e) => {
    e.preventDefault()
    const ratingsRowWithTable = revealRatingButton.previousElementSibling

    ratingsRowWithTable?.querySelectorAll('table tr').forEach((rating, index, rows) => {
      if (rating.classList.contains('is-last')) {
        rating.classList.remove('is-last')
      }

      rating.classList.remove('hidden')

      if (index === rows.length - 1) {
        rating.classList.add('is-last')
      }
    })

    revealRatingButton.classList.add('hidden')
    window.gtag && window.gtag('event', 'click', { event_category: 'reveal-ratings' })
  }
})
